import { motion, useInView } from "framer-motion";
import { useRef } from "react";

/**
 * Animated SVG icons that "draw" themselves on scroll.
 * Inspired by the geometric line-drawing style of dfdg.com.
 */

interface DrawIconProps {
  className?: string;
  size?: number;
  delay?: number;
  variant: "cart" | "leaf" | "heart" | "store" | "star";
}

const pathData: Record<DrawIconProps["variant"], { paths: string[]; viewBox: string }> = {
  cart: {
    viewBox: "0 0 64 64",
    paths: [
      "M8 8 L16 8 L24 40 L52 40 L56 16 L20 16",
      "M28 52 A4 4 0 1 0 28 44 A4 4 0 1 0 28 52",
      "M48 52 A4 4 0 1 0 48 44 A4 4 0 1 0 48 52",
    ],
  },
  leaf: {
    viewBox: "0 0 64 64",
    paths: [
      "M32 56 C32 56 8 40 8 24 C8 12 20 4 32 4 C44 4 56 12 56 24 C56 40 32 56 32 56Z",
      "M32 56 L32 20",
      "M32 32 L20 20",
      "M32 28 L44 16",
    ],
  },
  heart: {
    viewBox: "0 0 64 64",
    paths: [
      "M32 56 L8 32 C4 24 4 16 12 10 C20 4 28 8 32 16 C36 8 44 4 52 10 C60 16 60 24 56 32 L32 56Z",
    ],
  },
  store: {
    viewBox: "0 0 64 64",
    paths: [
      "M4 28 L12 8 L52 8 L60 28",
      "M4 28 L4 56 L60 56 L60 28",
      "M4 28 C4 28 12 36 20 28 C28 20 28 36 32 28 C36 20 44 36 44 28 C44 20 52 36 60 28",
      "M24 56 L24 40 L40 40 L40 56",
    ],
  },
  star: {
    viewBox: "0 0 64 64",
    paths: [
      "M32 4 L38 24 L60 24 L42 36 L48 56 L32 44 L16 56 L22 36 L4 24 L26 24 Z",
    ],
  },
};

const DrawIcon = ({ className = "", size = 120, delay = 0, variant }: DrawIconProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });
  const data = pathData[variant];

  return (
    <div ref={ref} className={className}>
      <svg
        width={size}
        height={size}
        viewBox={data.viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className="overflow-visible"
      >
        {data.paths.map((d, i) => (
          <motion.path
            key={i}
            d={d}
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 0.7 } : {}}
            transition={{
              pathLength: {
                duration: 2,
                delay: delay + i * 0.4,
                ease: [0.22, 1, 0.36, 1],
              },
              opacity: {
                duration: 0.4,
                delay: delay + i * 0.4,
              },
            }}
          />
        ))}
      </svg>
    </div>
  );
};

export default DrawIcon;